/**
 * Features Section - Clean bento grid layout
 */

import { ClipboardList, FileUp, Store, Tags, QrCode, Chrome } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../admin/EditableElement';

const features = [
  {
    id: 1,
    icon: ClipboardList,
    titleKey: 'features.projectChecklists.title',
    title: 'Project Checklists',
    descriptionKey: 'features.projectChecklists.description',
    description: 'Organize your shopping by project - kitchen renovation, dorm setup, or dream living room. Import from IKEA Planner or build manually.',
    size: 'large',
  },
  {
    id: 2,
    icon: FileUp,
    titleKey: 'features.ikeaPlannerImport.title',
    title: 'IKEA Planner Import',
    descriptionKey: 'features.ikeaPlannerImport.description',
    description: 'Upload your SEKTION kitchen, PAX wardrobe, or BESTA design. We create a checklist automatically and watch for deals on every item.',
    size: 'large',
    hasChart: true,
  },
  {
    id: 3,
    icon: Store,
    titleKey: 'features.inStoreAsIsVisibility.title',
    title: 'In-Store As-Is Visibility',
    descriptionKey: 'features.inStoreAsIsVisibility.description',
    description: 'See what\'s in the As-Is section at stores near you - without driving there. Our scanner network updates inventory in real-time.',
    size: 'small',
  },
  {
    id: 4,
    icon: Chrome,
    titleKey: 'features.browserExtension.title',
    title: 'Browser Extension',
    descriptionKey: 'features.browserExtension.description',
    description: 'Add items to your checklist with one click while browsing ikea.com. See if items are currently on sale. Chrome, Firefox, Safari.',
    size: 'wide',
    hasBrowser: true,
  },
  {
    id: 5,
    icon: QrCode,
    titleKey: 'features.scanAndTrack.title',
    title: 'Scan & Track',
    descriptionKey: 'features.scanAndTrack.description',
    description: 'At IKEA? Scan items to add to your checklist. Scan As-Is barcodes to check prices and alert others in the Rumio network.',
    size: 'small',
  },
  {
    id: 6,
    icon: Tags,
    titleKey: 'features.multiCategorySaleAlerts.title',
    title: 'Multi-Category Sale Alerts',
    descriptionKey: 'features.multiCategorySaleAlerts.description',
    description: 'Track Last Chance, Family Offers, Limited Time deals, and both online and in-store As-Is. Get notified across all sale types.',
    size: 'wide',
    hasMap: true,
  },
];

function PriceChartVisual() {
  return (
    <div className="mt-4 flex items-end gap-1 h-20">
      {[40, 55, 45, 60, 50, 70, 55, 45, 35].map((h, i) => (
        <div
          key={i}
          className={`flex-1 rounded-t transition-all duration-300 ${i === 8 ? 'bg-green-500' : 'bg-primary-200'}`}
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

function BrowserVisual({ trackLabel }: { trackLabel: string }) {
  return (
    <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <div className="flex-1 mx-2">
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
      </div>
      <div className="p-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
          <span className="text-white font-bold text-sm">R</span>
        </div>
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-24 mb-2" />
          <div className="h-2 bg-gray-100 rounded w-16" />
        </div>
        <div className="px-3 py-1.5 bg-accent rounded-lg text-white text-xs font-semibold">
          {trackLabel}
        </div>
      </div>
    </div>
  );
}

function MapVisual({ countriesLabel }: { countriesLabel: string }) {
  return (
    <div className="mt-4 relative h-24 bg-primary-50 rounded-lg overflow-hidden">
      {/* Simplified world map dots */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-full h-full">
          {/* Map dots representing countries */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
          <div className="absolute top-1/3 left-1/2 w-3 h-3 bg-primary-600 rounded-full animate-pulse animation-delay-100" />
          <div className="absolute top-1/2 left-1/3 w-2 h-2 bg-primary-400 rounded-full animate-pulse animation-delay-200" />
          <div className="absolute top-1/4 right-1/4 w-2.5 h-2.5 bg-primary-500 rounded-full animate-pulse animation-delay-300" />
          <div className="absolute bottom-1/3 right-1/3 w-2 h-2 bg-primary-400 rounded-full animate-pulse animation-delay-400" />
          <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-primary-600 rounded-full animate-pulse" />
          <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-primary-400 rounded-full animate-pulse animation-delay-200" />
        </div>
      </div>
      <div className="absolute bottom-2 right-2 px-2 py-1 bg-white/80 backdrop-blur-sm rounded text-xs font-semibold text-primary-600">
        {countriesLabel}
      </div>
    </div>
  );
}

export default function Features() {
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('features', key, translatedFallback) ?? translatedFallback;
  };

  const sizeClasses: Record<string, string> = {
    small: 'lg:col-span-1',
    large: 'lg:col-span-2 lg:row-span-1',
    wide: 'md:col-span-2 lg:col-span-2',
  };

  return (
    <section id="features" className="py-20 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <EditableElement section="features" field="badge">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 rounded-full text-sm font-medium text-primary-700 mb-4">
              {get('badge', 'Features')}
            </div>
          </EditableElement>
          <EditableElement section="features" field="title">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              {get('title', 'Everything you need to')}
              <span className="text-primary-600">
                {' '}{get('title_highlight', 'shop smarter')}
              </span>
            </h2>
          </EditableElement>
          <EditableElement section="features" field="subtitle">
            <p className="mt-4 text-lg text-gray-600">
              {get('subtitle', 'From project planning to deal alerts - everything you need to save on IKEA furniture.')}
            </p>
          </EditableElement>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;
            const title = get(`feature${feature.id}_title`, feature.title);
            const description = get(`feature${feature.id}_desc`, feature.description);

            return (
              <EditableElement key={feature.id} section="features" field={`feature${feature.id}_title`}>
                <div
                  className={`
                    group relative p-6 rounded-2xl border border-gray-200 bg-gray-50
                    hover:bg-white hover:shadow-lg hover:border-primary-200
                    transition-all duration-300 hover:-translate-y-1
                    ${sizeClasses[feature.size]}
                  `}
                >
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-primary-600 text-white shadow-md group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{description}</p>

                  {/* Visual elements for specific cards */}
                  {feature.hasChart && <PriceChartVisual />}
                  {feature.hasBrowser && <BrowserVisual trackLabel={t('features.trackLabel', 'Track')} />}
                  {feature.hasMap && <MapVisual countriesLabel={t('features.countriesLabel', '50+ Countries')} />}
                </div>
              </EditableElement>
            );
          })}
        </div>
      </div>
    </section>
  );
}
